import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Upload, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, Skeleton } from '@/components/feedback';
import { ColumnOrderMenu } from '@/components/table/ColumnOrderMenu';
import { usePersistedColumnOrder } from '@/components/table/usePersistedColumnOrder';

export interface ColumnDef<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  accessor?: (row: T) => unknown;
  className?: string;
}

interface CatalogTableProps<T extends { id: string }> {
  columns: ColumnDef<T>[];
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters?: ReactNode;
  onRowEdit?: (row: T) => void;
  onRowDelete?: (row: T) => void;
  renderExpandedRow?: (row: T) => ReactNode;
  onAddNew?: () => void;
  onImport?: () => void;
  onExport?: () => void;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyMessage?: string;
  /** Test-id prefix so multiple tables on a page don't collide. */
  testId?: string;
}

/**
 * Generic admin catalog table with search + pagination + Add/Import/Export
 * toolbar + per-row Edit/Delete actions.
 *
 * Used by Materials and Edges admin pages in this PR; future PRs (drawer
 * systems / hardware / lighting in PR #8) will reuse it as-is.
 */
export function CatalogTable<T extends { id: string }>(props: CatalogTableProps<T>) {
  const { t } = useTranslation();
  const {
    columns,
    data,
    total,
    page,
    pageSize,
    onPageChange,
    searchValue,
    onSearchChange,
    filters,
    onRowEdit,
    onRowDelete,
    renderExpandedRow,
    onAddNew,
    onImport,
    onExport,
    loading,
    error,
    onRetry,
    emptyMessage,
    testId = 'catalog-table',
  } = props;

  const [searchInput, setSearchInput] = useState(searchValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setSearchInput(searchValue), [searchValue]);

  const onSearchTyped = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(value), 300);
  };

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const showActions = Boolean(onRowEdit || onRowDelete);
  const tableColumns: ColumnDef<T>[] = [
    ...columns,
    ...(showActions
      ? [
          {
            key: 'actions',
            label: t('common.actions', { defaultValue: 'Actions' }),
            className: 'w-32 text-right',
            render: (row: T) => (
              <div className="inline-flex items-center gap-1">
                {onRowEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    aria-label={t('common.edit', { defaultValue: 'Edit' })}
                    onClick={() => onRowEdit(row)}
                    data-testid={`${testId}-edit-${row.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {onRowDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    aria-label={t('common.delete', { defaultValue: 'Delete' })}
                    onClick={() => onRowDelete(row)}
                    data-testid={`${testId}-delete-${row.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];
  const { orderedColumns, moveColumn, resetColumnOrder } = usePersistedColumnOrder(
    `catalog-admin.${testId}`,
    tableColumns,
  );

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={searchInput}
          onChange={(e) => onSearchTyped(e.target.value)}
          placeholder={t('common.search', { defaultValue: 'Search' })}
          className="max-w-xs"
          aria-label={t('common.search', { defaultValue: 'Search' })}
          data-testid={`${testId}-search`}
        />
        {filters}
        <div className="ml-auto flex items-center gap-2">
          <ColumnOrderMenu
            columns={orderedColumns}
            onMoveColumn={moveColumn}
            onReset={resetColumnOrder}
          />
          {onImport && (
            <Button
              type="button"
              variant="outline"
              onClick={onImport}
              data-testid={`${testId}-import`}
            >
              <Upload className="mr-2 h-4 w-4" />
              {t('catalogAdmin.import', { defaultValue: 'Import' })}
            </Button>
          )}
          {onExport && (
            <Button
              type="button"
              variant="outline"
              onClick={onExport}
              data-testid={`${testId}-export`}
            >
              <Download className="mr-2 h-4 w-4" />
              {t('catalogAdmin.export', { defaultValue: 'Export' })}
            </Button>
          )}
          {onAddNew && (
            <Button type="button" onClick={onAddNew} data-testid={`${testId}-add`}>
              <Plus className="mr-2 h-4 w-4" />
              {t('common.add', { defaultValue: 'Add' })}
            </Button>
          )}
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <Table>
          <THead>
            <TR>
              {orderedColumns.map((col) => (
                <TH key={col.key} className={col.className}>
                  {col.label}
                </TH>
              ))}
            </TR>
          </THead>
          <TBody>
            {loading && data.length === 0 ? (
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <TR key={rowIndex}>
                  {Array.from({ length: orderedColumns.length }).map((__, colIndex) => (
                    <TD key={colIndex} className="py-3">
                      <Skeleton className="h-4 w-full max-w-[180px]" />
                    </TD>
                  ))}
                </TR>
              ))
            ) : data.length === 0 ? (
              <TR>
                <TD colSpan={orderedColumns.length}>
                  <EmptyState description={emptyMessage} />
                </TD>
              </TR>
            ) : (
              data.map((row) => {
                const expanded = renderExpandedRow?.(row);
                const colSpan = orderedColumns.length;
                return (
                  <Fragment key={row.id}>
                    <TR data-testid={`${testId}-row-${row.id}`}>
                      {orderedColumns.map((col) => (
                        <TD key={col.key} className={col.className}>
                          {col.render
                            ? col.render(row)
                            : col.accessor
                              ? String(col.accessor(row) ?? '')
                              : ''}
                        </TD>
                      ))}
                    </TR>
                    {expanded && (
                      <TR
                        className="bg-muted/20 hover:bg-muted/20"
                        data-testid={`${testId}-expanded-${row.id}`}
                      >
                        <TD colSpan={colSpan} className="p-0">
                          {expanded}
                        </TD>
                      </TR>
                    )}
                  </Fragment>
                );
              })
            )}
          </TBody>
        </Table>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {t('catalogAdmin.pagination', {
            defaultValue: 'Page {{page}} of {{lastPage}} · {{total}} items',
            page,
            lastPage,
            total,
          })}
        </span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            className="h-8 px-2"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
            aria-label={t('common.previous', { defaultValue: 'Previous' })}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-8 px-2"
            disabled={page >= lastPage || loading}
            onClick={() => onPageChange(page + 1)}
            aria-label={t('common.next', { defaultValue: 'Next' })}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
